import { Upload, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { FileUploadProgress } from "components/upload/FileUploadProgress";
import { MAX_UPLOAD_ZIP_MB, useGeometryUpload } from "components/boundary";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { ClipConfiguration } from "../../models";
import { BoundaryToolProps } from "./define-boundary";

export const UploadComponent = (
  props: ClipConfigProps & ClipStepsControlProps & BoundaryToolProps,
) => {
  const {
    uploadState,
    rejectionMessage,
    uploadProgress,
    isUploading,
    haveUploaded,
    files,
    getRootProps,
    getInputProps,
    cancelUpload,
  } = useGeometryUpload({
    // Save immediately: the clip must exist in DynamoDB to start the conversion and for the editor to poll it.
    onZipUploaded: (s3Key) => {
      // Shown as processing straight away, not once the save returns, so the drop zone doesn't flash back.
      const next: ClipConfiguration = {
        ...props.clipConfig,
        boundaryChange: { uploaded_zip_s3_key: s3Key },
        geometryProcessing: "processing",
      };
      props.setClipConfig(next);
      void props.saveClipConfig(next).then((saved) => {
        if (!saved)
          props.setClipConfig((prev) => ({
            ...prev,
            geometryProcessing: undefined,
          }));
      });
    },
  });

  // Read from the clip, not this tab, so "Processing" shows again when a clip is reopened mid-upload.
  const processing = props.clipConfig.geometryProcessing;

  if (processing === "processing") {
    return (
      <div className="flex flex-col justify-center items-center mt-4 gap-2 text-center">
        <Spinner />
        <p className="text-sm">
          Processing the uploaded boundary. A large file can take a minute; it
          will appear on the map when it's ready. You can close the clip and
          come back later.
        </p>
      </div>
    );
  }

  // A clip holds one uploaded file. To use another, the user removes this one first.
  if (props.hasUploadedBoundary) {
    return (
      <Alert className="mt-4">
        <AlertDescription>
          {uploadState === "uploaded"
            ? `Boundary processed: ${(props.clipConfig.geometry?.features.length ?? 0).toLocaleString()} feature(s) added.`
            : "This clip already has an uploaded boundary."}{" "}
          To upload a different file, remove it below first.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      {processing === "failed" && (
        <Alert className="mt-4" variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription>
            {props.clipConfig.failureReason ??
              "The uploaded boundary could not be processed."}{" "}
            Upload the file again, or try a smaller file.
          </AlertDescription>
        </Alert>
      )}
      {rejectionMessage && uploadState !== "uploading" && (
        <Alert className="mt-4" variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription>{rejectionMessage}</AlertDescription>
        </Alert>
      )}
      {uploadState === "uploading" ? (
        <FileUploadProgress
          uploadProgress={uploadProgress}
          file={files[0]}
          onRemove={cancelUpload}
          isUploading={isUploading}
          haveUploaded={haveUploaded}
        />
      ) : (
        <div
          {...getRootProps()}
          className="border-2 border-dashed border-muted-foreground/40 m-2 p-2 text-center cursor-pointer rounded-lg dropzone"
        >
          <input {...getInputProps({ multiple: false })} />
          <Upload className="size-5 text-info mx-auto mb-1" />
          <p className="text-sm">Click to upload or drag and drop file</p>
          <p className="text-xs text-muted-foreground">
            One ZIP boundary file per clip, up to {MAX_UPLOAD_ZIP_MB} MB
          </p>
        </div>
      )}
    </div>
  );
};
